import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import PrerenderStatus from "@/components/common/PrerenderStatus";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import ImageLightbox from "@/components/common/ImageLightbox";
import AgentCard from "@/components/common/AgentCard";
import MoreBySellerStrip from "@/components/common/MoreBySellerStrip";
import VerifiedBadge from "@/components/common/VerifiedBadge";
import { formatNZD, timeAgo, phoneDigits } from "@/lib/format";
import { extractAdId } from "@/lib/slugs";
import { useAuth } from "@/lib/auth";
import { pushRecent } from "@/hooks/useRecentlyViewed";
import { AD } from "@/constants/testIds";
import { Phone, MessageCircle, MessageSquare, MapPin, Eye, Loader2, ShieldCheck, Maximize2, ImageOff, Link2, LogIn } from "lucide-react";
import { toast } from "sonner";
import PropertyAdView from "@/pages/PropertyAdView";
import DetailsRow from "@/components/detail/DetailsRow";
import CarJamReportCta from "@/components/detail/CarJamReportCta";
import CarBackgroundCheck from "@/components/detail/CarBackgroundCheck";
import CarRatings from "@/components/detail/CarRatings";
import CarFeaturesChips from "@/components/detail/CarFeaturesChips";
import BikeDetailsCard from "@/components/detail/BikeDetailsCard";
import ArrangeViewingCard from "@/components/detail/ArrangeViewingCard";
import QuickApplyCta from "@/components/detail/QuickApplyCta";
import SharePostCard from "@/components/detail/SharePostCard";
import FeedbackCard from "@/components/detail/FeedbackCard";
import ReviewCard, { StarSummary } from "@/components/detail/ReviewCard";
import ShippingOptions from "@/components/detail/ShippingOptions";
import QuestionsAndAnswers from "@/components/detail/QuestionsAndAnswers";
import SellerCard from "@/components/detail/SellerCard";
import FavoriteButton from "@/components/common/FavoriteButton";
import { fetchTranslation, BY_CODE } from "@/components/detail/TranslationSwitcher";
import useLangPref from "@/hooks/useLangPref";
import SellerChatFab from "@/components/detail/SellerChatFab";
import ListingFooterRow from "@/components/detail/ListingFooterRow";
import ListingDocuments from "@/components/detail/ListingDocuments";
import { gaEvent } from "@/lib/ga";
import { imgVariant } from "@/lib/tileSrc";

const FALLBACK = "https://images.unsplash.com/photo-1607435097405-db48f377bff6?auto=format&fit=crop&w=1200&q=60";

export default function AdDetailPage() {
  const { adId, adSlug } = useParams();
  const id = adId || extractAdId(adSlug || "");
  const nav = useNavigate();
  const { user } = useAuth();

  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [revealed, setRevealed] = useState(null); // { phone, whatsapp, seller_name }
  // Iter-276 PART 2 — translated title/description swap-in-place. The
  // ORIGINAL text stays in `listing.*` and share/SEO always uses those
  // fields; only the visible header + description use these overrides.
  const [translation, setTranslation] = useState(null); // { lang, title, description }
  const [langPref] = useLangPref();

  const q = useQuery({
    queryKey: ["ad", id],
    queryFn: async () => {
      const { data } = await sharedApi.get(`/classifieds/listings/${id}`);
      return data;
    },
    enabled: !!id,
  });

  // WO 800 §5 — document-accurate structured data + canonical/title/
  // description come from the shared backend's SEO endpoint (Car /
  // Motorcycle / RealEstateListing / Product built from stored fields).
  const seoQ = useQuery({
    queryKey: ["ad-seo", id],
    queryFn: async () => {
      const { data } = await sharedApi.get(`/classifieds/seo/listing/${id}`);
      return data;
    },
    enabled: !!id,
    retry: false,
    staleTime: 10 * 60 * 1000,
  });
  const adSeo = seoQ.data;

  // Iter-276f — auto-translate on mount / on language pref change.
  // Users select their language via the persistent Header pill; the
  // listing page reacts by firing a translate call whenever the pref
  // differs from the currently-rendered translation. English clears.
  useEffect(() => {
    if (!id || !q.data) return;
    if (langPref === "en") {
      if (translation) setTranslation(null);
      return;
    }
    if (translation?.lang === langPref) return;
    if (!BY_CODE[langPref]) return;
    let cancelled = false;
    (async () => {
      try {
        const t = await fetchTranslation(id, langPref);
        if (!cancelled) setTranslation(t);
      } catch {
        // interactive picks toast inside the switcher; on auto-translate
        // we stay silent so a rate-limit doesn't spam.
      }
    })();
    return () => { cancelled = true; };
  }, [id, langPref, q.data, translation]);

  const revealMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post(`/classifieds/listings/${id}/contact`);
      return data;
    },
    onSuccess: (data) => {
      setRevealed(data);
      gaEvent("generate_lead", { item_id: id, method: "call" });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Could not reveal contact");
    },
  });

  const startChat = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/chats", { listing_id: id, message: "Kia Ora — is this still available?" });
      return data;
    },
    onSuccess: (data) => {
      gaEvent("generate_lead", { item_id: id, method: "chat" });
      const chatId = data.id || data.chat_id;
      if (chatId) {
        toast.success("Chat started");
        nav(`/chat/${chatId}`);
      } else {
        // Backend didn't return an id — fall back to the inbox so the
        // seller can find the new thread there instead of landing on a
        // blank /chat/ URL.
        toast.success("Chat started — find it in your inbox.");
        nav("/chat");
      }
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not start chat"),
  });

  const handleReveal = () => {
    if (!user) { nav(`/login?next=${encodeURIComponent(window.location.pathname)}`); return; }
    revealMut.mutate();
  };

  // Track this ad in the client-side "recently viewed" store for the homepage rail.
  // MUST be declared before any early return to satisfy the Rules of Hooks.
  useEffect(() => {
    if (q.data?.id) {
      pushRecent(q.data.id);
      // Iter 741 — GA4 recommended event (fires once per ad load).
      gaEvent("view_item", {
        item_id: q.data.id,
        item_name: q.data.title,
        item_category: q.data.category_slug,
        price: q.data.price,
      });
    }
  }, [q.data?.id]);

  // Reset gallery state when SPA-navigating between ad-detail pages
  // (e.g. via "More by Seller"). Without this, a high activeImg from
  // the previous listing can point past the new listing's images array
  // and render a blank main image. Also close any open lightbox.
  useEffect(() => {
    setActiveImg(0);
    setLightboxOpen(false);
    setRevealed(null);
  }, [id]);

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data) {
    // WO 800 §5 — removed/expired listings are 410 Gone to crawlers; a
    // malformed id is a plain 404. Mirrors the /api/seo/render behaviour.
    const gone =
      seoQ.error?.response?.status === 410 || /^(?:ad|cl)_[A-Za-z0-9]{6,}$/.test(id || "");
    return <NotFound status={gone ? 410 : 404} />;
  }
  const listing = q.data;
  // SAFETY-HOLD (iter-224): pending_review / rejected listings must NEVER
  // be visible to the public. Non-owners see the standard NotFound screen
  // (indistinguishable from a truly-missing listing to avoid leaking
  // that a listing exists but is under review). Owners are redirected to
  // /my-ads where the amber/red banner explains the status.
  const status = String(listing.status || "active").toLowerCase();
  const isOwner = user && (user.id === listing.seller_id || user.id === listing.owner_id);
  if ((status === "pending_review" || status === "rejected") && !isOwner) {
    return <NotFound status={404} />;
  }

  // Property ads get the full legacy layout (map tabs, viewing form, amenities, etc.)
  // Iter-259 §fix: property child categories (parent_category_slug ===
  // "property") must also route through the specialised PropertyAdView
  // layout — not just the exact root slug. Ensures the "for-sale-houses-
  // apartments" leaf keeps its schools map, floor plans, etc.
  if (
    listing.category_slug === "property" ||
    listing.parent_category_slug === "property"
  ) {
    return <PropertyAdView listing={listing} seo={adSeo} />;
  }
  const hasPhotos = Array.isArray(listing.images) && listing.images.length > 0;
  const images = hasPhotos ? listing.images : [];
  const attrs = listing.attributes || {};
  const hasAgency = !!(attrs["Agency Name"] || attrs["Agency Logo"]);
  // Iter-263: jobs-family gate. Hides viewing/shipping/reviews and
  // routes the price sidebar + primary CTA to the Quick Apply flow.
  const isJob =
    listing.parent_category_slug === "jobs" ||
    listing.category_slug === "jobs";

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = `${origin}${window.location.pathname}`;
  const priceNum = Number(listing.price);
  const fallbackLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin || "/" },
        {
          "@type": "ListItem",
          position: 2,
          name: listing.category_name || listing.category_slug,
          item: `${origin}/c/${listing.category_slug}`,
        },
        { "@type": "ListItem", position: 3, name: listing.title, item: canonical },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: listing.title,
      description: listing.description || "",
      image: hasPhotos ? images.slice(0, 4) : [],
      url: canonical,
      category: listing.category_name || listing.category_slug,
      ...(priceNum > 0 && {
        offers: {
          "@type": "Offer",
          price: priceNum,
          priceCurrency: "NZD",
          availability: "https://schema.org/InStock",
          url: canonical,
          areaServed: listing.city || listing.region || "New Zealand",
          seller: {
            "@type": listing.seller_verified ? "Organization" : "Person",
            name: listing.seller_name || "Allsale seller",
          },
        },
      }),
    },
  ];
  // WO 800 §5 — endpoint payload wins; client-built blocks are the fallback
  // while the endpoint resolves or if it's unreachable.
  const jsonLd = adSeo?.json_ld?.length ? adSeo.json_ld : fallbackLd;

  const copyListingLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(canonical);
      } else {
        const ta = document.createElement("textarea");
        ta.value = canonical;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Copied!");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  return (
    <div data-testid={AD.root} className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12 pb-28 lg:pb-12">
      {status === "pending_review" && isOwner && (
        <div
          data-testid="ad-held-watermark"
          className="mb-4 rounded-2xl border-2 border-dashed border-amber-400 bg-amber-50 text-amber-900 px-4 py-3 flex items-center gap-2.5"
          role="status"
          aria-live="polite"
        >
          <ShieldCheck className="w-4 h-4 shrink-0" />
          <p className="text-sm font-semibold">
            ✔ Under review — only you can see this.
          </p>
        </div>
      )}
      <Seo
        title={adSeo?.title || listing.title}
        description={adSeo?.description || `${listing.title} — ${formatNZD(listing.price)} in ${listing.city || listing.region || "New Zealand"}. ${(listing.description || "").slice(0, 120)}`}
        image={images[0]}
        url={adSeo?.canonical || canonical}
        type="product"
        jsonLd={jsonLd}
        robots={adSeo?.robots && adSeo.robots !== "index,follow" ? adSeo.robots : undefined}
      />
      {/* Iter-258: Breadcrumb sits INSIDE the responsive shell so on
          mobile the source order can flow Hero → Breadcrumb → Title
          via CSS `order`. On desktop the breadcrumb spans the full width
          above the two-column grid. */}
      <div className="mt-6 flex flex-col gap-6 lg:grid lg:grid-cols-12 lg:gap-8">
        <div className="order-2 lg:col-span-12 lg:order-none lg:mb-0" data-testid="ad-breadcrumb-wrapper">
          <Breadcrumbs
            items={[
              ...(listing.parent_category_id && listing.parent_category_name
                ? [{
                    label: listing.parent_category_name,
                    to: `/c/${listing.parent_category_slug || listing.parent_category_id}`,
                  }]
                : []),
              { label: listing.category_name || listing.category_slug, to: `/c/${listing.category_slug}` },
              { label: listing.title },
            ]}
          />
        </div>

        {/* LEFT column on desktop; on mobile the wrapper vanishes via
            display:contents so every child participates in the parent
            flex-col and its `order-N` reflects the 14-block spec. */}
        <div className="contents lg:block lg:col-span-8 lg:space-y-6">
          <div data-testid={AD.gallery} className="order-1 lg:order-none rounded-3xl overflow-hidden bg-white shadow-card">
            <div className="aspect-[16/10] bg-slate-100 relative group">
              {hasPhotos ? (
                <>
                  <img
                    src={imgVariant(images[activeImg], 1600)}
                    alt={listing.title}
                    className="w-full h-full object-cover cursor-zoom-in"
                    onClick={() => setLightboxOpen(true)}
                    onError={(e) => { e.currentTarget.src = FALLBACK; }}
                  />
                  {listing.is_featured && <span className="ribbon">Featured</span>}
                  <button
                    type="button"
                    onClick={() => setLightboxOpen(true)}
                    data-testid="ad-gallery-expand"
                    aria-label="View photos full-screen"
                    className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 transition-colors"
                  >
                    <Maximize2 className="w-3.5 h-3.5" /> {images.length} photo{images.length === 1 ? "" : "s"}
                  </button>
                </>
              ) : (
                <div
                  data-testid="ad-gallery-empty"
                  className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-400"
                >
                  <ImageOff className="w-10 h-10" strokeWidth={1.5} />
                  <span className="text-sm font-semibold">No photos</span>
                </div>
              )}
            </div>
            {hasPhotos && images.length > 1 && (
              <div className="p-3 flex gap-2 overflow-x-auto no-scrollbar">
                {images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => { setActiveImg(i); if (i === activeImg) setLightboxOpen(true); }}
                    onDoubleClick={() => { setActiveImg(i); setLightboxOpen(true); }}
                    data-testid={`ad-thumb-${i}`}
                    className={`shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 ${i === activeImg ? "border-[color:var(--brand-blue)]" : "border-transparent"} transition-colors`}
                  >
                    <img src={imgVariant(src, 320, 70)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="order-3 lg:order-none rounded-3xl bg-white shadow-card p-6 md:p-8">
            <div className="flex items-start gap-3">
              <h1 data-testid={AD.title} className="flex-1 font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
                {translation?.title || listing.title}
              </h1>
              <FavoriteButton listingId={id} className="shrink-0 mt-1" />
            </div>
            {translation && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span
                  data-testid="clf-txn-chip"
                  className="inline-flex items-center gap-1 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest"
                >
                  🌐 Translated · {translation.lang}
                </span>
                <button
                  type="button"
                  data-testid="clf-txn-show-original"
                  onClick={() => setTranslation(null)}
                  className="text-xs font-semibold text-slate-500 hover:text-[color:var(--brand-blue)] hover:underline"
                >
                  Show original
                </button>
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {listing.city || listing.region || "New Zealand"}</span>
              <span>•</span>
              <span>{timeAgo(listing.created_at)}</span>
              <span>•</span>
              <span className="inline-flex items-center gap-1"><Eye className="w-4 h-4" /> {listing.views ?? 0} views</span>
              {listing.listing_no && (
                <>
                  <span>•</span>
                  <span
                    className="text-slate-400"
                    data-testid="ad-listing-no"
                  >
                    Listing No {listing.listing_no}
                  </span>
                </>
              )}
              {(listing.review_count ?? 0) > 0 && (
                <StarSummary
                  avg={listing.review_avg}
                  count={listing.review_count}
                />
              )}
            </div>
          </div>

          {/* Iter-258 §5-9: Details/Background/Features/Description/
              Ratings + Iter-258 §10 (bikes only) BikeDetailsCard. Split
              into its own card so the mobile source order can place it
              AFTER the Price card (order-4) below. */}
          <div className="order-5 lg:order-none rounded-3xl bg-white shadow-card p-6 md:p-8">
            <DetailsRow listing={listing} />
            {/* Cars-family blocks. Gated on `parent_category_slug`
                (backend enriches this on read; old listings still get
                the mirror) with a fallback to the leaf slug. Order per
                bulletin: Background check → Features → Description →
                Ratings. */}
            {(listing.parent_category_slug === "cars" ||
              listing.category_slug === "cars") && (
              <>
                <CarBackgroundCheck listing={listing} />
                <CarFeaturesChips listing={listing} />
              </>
            )}
            <h2 className="font-display text-lg font-bold text-slate-800 mb-3">Description</h2>
            <p
              data-testid={AD.description}
              className="text-slate-600 leading-relaxed whitespace-pre-line"
            >
              {translation?.description || listing.description}
            </p>
            {/* Iter-387 — attached report PDFs (e.g. Consumer Information
                Notices recovered in the cars migration). */}
            <ListingDocuments listing={listing} />
            {(listing.parent_category_slug === "cars" ||
              listing.category_slug === "cars") && (
              <CarRatings listing={listing} />
            )}
            {(listing.parent_category_slug === "bikes" ||
              listing.category_slug === "bikes") && (
              <BikeDetailsCard listing={listing} />
            )}
            {isJob && <QuickApplyCta listing={listing} />}
            {!isJob && <ArrangeViewingCard listingId={listing.id} />}
            {!isJob && <ShippingOptions listing={listing} />}
            <QuestionsAndAnswers
              listingId={listing.id}
              sellerId={listing.seller?.id || listing.seller_id || listing.user_id}
            />
          </div>

          {/* Iter-258 §12-13 post-detail block order — Share (12) → CarJam (13).
              Reviews/feedback sit below as the "reviews surface" and use
              order-13/14 to stay after the CarJam CTA. */}
          <div className="order-12 lg:order-none mt-0 lg:mt-6 space-y-4">
            <SharePostCard url={canonical} title={listing.title} />
            {listing.carjam_report_url && (
              <CarJamReportCta
                url={listing.carjam_report_url}
                plate={attrs.Plate || attrs.plate}
              />
            )}
            <FeedbackCard listingId={listing.id} />
            {!isJob && (
              <ReviewCard
                listingId={listing.id}
                ownerId={listing.seller?.id || listing.seller_id || listing.user_id}
                avg={listing.review_avg}
                count={listing.review_count}
              />
            )}
          </div>

          {/* Iter-258 §14 sticky footer — utility row (Share / views /
              Report). On mobile the row is followed by a fixed
              bottom-anchored Chat/Show-contact bar rendered further
              down (outside the grid). */}
          <div className="order-[99] lg:order-none">
            <ListingFooterRow listing={listing} />
          </div>
        </div>

        {/* RIGHT column on desktop; on mobile the wrapper vanishes via
            display:contents so children participate in the parent
            flex-col using their own `order-N`. */}
        {/* Iter-104 fix: cap the sticky column height and let it scroll
            internally when the sidebar is taller than the viewport
            (short-screen laptops, seller card + arrange-a-viewing +
            share card can easily push past 800px on 1080p). Without
            this, `ad-seller-business-chip` and everything below the
            fold pins off-screen. */}
        <div className="contents lg:block lg:col-span-4 lg:sticky lg:top-28 lg:self-start lg:space-y-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto lg:pr-2">
            <div className="order-4 lg:order-none rounded-3xl bg-white shadow-card p-6" data-testid="sidebar-price-card">
              {isJob ? (
                <>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                    Salary
                  </div>
                  <div
                    data-testid="sidebar-job-salary"
                    className="mt-1 font-display text-2xl font-black text-[color:var(--brand-blue)]"
                  >
                    {attrs.Salary || attrs["Salary Range"] || "On application"}
                  </div>
                  {(attrs["Job Type"] || attrs["Employment Type"]) && (
                    <div
                      data-testid="sidebar-job-type"
                      className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide"
                    >
                      {attrs["Job Type"] || attrs["Employment Type"]}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Asking price</div>
                  <div data-testid={AD.price} className="mt-1 font-display text-4xl font-black text-[color:var(--brand-blue)]">
                    {formatNZD(listing.price)}
                  </div>
                </>
              )}
              {/* Legacy sidebar facts: Address / Listing No / Date */}
              <dl className="mt-4 pt-4 border-t border-subtle divide-y divide-subtle text-sm">
                {(listing.area || listing.city || listing.region) && (
                  <div className="flex items-start justify-between gap-3 py-2">
                    <dt className="text-slate-500 shrink-0">Address</dt>
                    <dd
                      data-testid="sidebar-address"
                      className="font-semibold text-slate-800 text-right"
                    >
                      {[listing.area, listing.city, listing.region, "New Zealand"]
                        .filter(Boolean)
                        .join(", ")}
                    </dd>
                  </div>
                )}
                {(listing.public_ref || listing.reference_number || listing.listing_no) && (
                  <div className="flex items-center justify-between gap-3 py-2">
                    <dt className="text-slate-500">Listing No</dt>
                    <dd className="flex items-center gap-1.5">
                      <span
                        data-testid="sidebar-listing-no"
                        className="font-mono font-semibold text-slate-800"
                      >
                        {listing.public_ref || listing.reference_number || listing.listing_no}
                      </span>
                      <button
                        type="button"
                        onClick={copyListingLink}
                        data-testid="sidebar-copy-link-btn"
                        aria-label="Copy listing link"
                        title="Copy listing link"
                        className="p-1 rounded text-slate-400 hover:text-[color:var(--brand-blue)] hover:bg-slate-100 transition-colors"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                      </button>
                    </dd>
                  </div>
                )}
                {listing.created_at && (
                  <div className="flex items-center justify-between gap-3 py-2">
                    <dt className="text-slate-500">Date posted</dt>
                    <dd
                      data-testid="sidebar-date-posted"
                      className="font-semibold text-slate-800"
                    >
                      {new Date(listing.created_at).toLocaleDateString("en-NZ", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
            {/* Iter-258 §11 Seller/agent card + contact reveal. On mobile
                lives at order-11 (after Ratings/BikeDetails, before
                Share). On desktop stacks below the price card in the
                sticky sidebar. */}
            <div className="order-11 lg:order-none rounded-3xl bg-white shadow-card p-6" data-testid="sidebar-contact-card">
              {!hasAgency && (
                <>
              <div className="flex items-center gap-3 pb-6 border-b border-subtle" data-testid="sidebar-seller-card">
                <div className="w-12 h-12 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] font-bold grid place-items-center">
                  {(listing.seller_name || "S").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Posted by</div>
                  <div className="font-semibold text-slate-800 truncate inline-flex items-center gap-1">
                    {listing.seller_name}
                    <VerifiedBadge verified={listing.seller_verified} />
                  </div>
                  {listing.seller_id && (
                    <Link
                      to={`/seller/${listing.seller_id}`}
                      data-testid="sidebar-view-seller"
                      className="text-xs font-semibold text-[color:var(--brand-blue)] hover:underline"
                    >
                      View all ads →
                    </Link>
                  )}
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {/* Iter-375 — anonymous visitors get one explicit sign-in
                    CTA (redirects back to this ad); contact reveal buttons
                    render only when signed in. */}
                {!user ? (
                  <button
                    data-testid="ad-signin-contact-cta"
                    onClick={handleReveal}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-white bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] transition-colors"
                  >
                    <LogIn className="w-4 h-4" /> Sign in to view contact details
                  </button>
                ) : (
                <>
                <button
                  data-testid={AD.revealPhoneBtn}
                  onClick={handleReveal}
                  disabled={revealMut.isPending}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-white bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] disabled:opacity-70 transition-colors"
                >
                  {revealMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                  {revealed?.phone ? (
                    <span data-testid={AD.phoneNumber}>{revealed.phone}</span>
                  ) : (
                    "Show phone number"
                  )}
                </button>

                {revealed?.whatsapp ? (
                  <a
                    data-testid={AD.whatsappLink}
                    href={`https://wa.me/${phoneDigits(revealed.whatsapp)}?text=${encodeURIComponent(`Kia Ora — I'm interested in your ad "${listing.title}" on Allsale Classified.`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-white transition-colors"
                    style={{ backgroundColor: "var(--whatsapp)" }}
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp seller
                  </a>
                ) : (
                  <button
                    data-testid={AD.revealWhatsappBtn}
                    onClick={handleReveal}
                    disabled={revealMut.isPending}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-white disabled:opacity-70 transition-colors"
                    style={{ backgroundColor: "var(--whatsapp)" }}
                  >
                    <MessageCircle className="w-4 h-4" /> Show WhatsApp
                  </button>
                )}
                </>
                )}

                {listing.allow_chat && (
                  <button
                    data-testid={AD.chatSellerBtn}
                    onClick={() => user ? startChat.mutate() : nav(`/login?next=${encodeURIComponent(window.location.pathname)}`)}
                    disabled={startChat.isPending}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-[color:var(--brand-blue)] bg-[color:var(--brand-blue-wash)] hover:bg-white border border-[color:var(--brand-blue)] transition-colors"
                  >
                    {startChat.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                    Chat with seller
                  </button>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-subtle flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                Meet in a public place. Never send money before viewing.
              </div>
                </>
              )}
            </div>

            {hasAgency && (
              <div className="order-11 lg:order-none">
                <AgentCard
                  listing={listing}
                  attrs={attrs}
                  revealed={revealed}
                  revealMut={revealMut}
                  onReveal={handleReveal}
                />
              </div>
            )}

            {!hasAgency && (
              <div
                className="order-11 lg:order-none rounded-3xl bg-white shadow-card p-6"
                data-testid="sidebar-seller-card-wrapper"
              >
                <SellerCard listing={listing} />
              </div>
            )}

            <Link
              to={`/c/${listing.category_slug}`}
              className="order-11 lg:order-none block text-center text-sm font-semibold text-[color:var(--brand-blue)] hover:underline"
            >
              More in {listing.category_name || listing.category_slug}
            </Link>
        </div>
      </div>

      <MoreBySellerStrip
        sellerId={listing.seller_id}
        sellerName={listing.seller_name}
        currentAdId={listing.id}
      />

      {/* Iter-258 §14: mobile-only sticky contact bar. Sits fixed at the
          bottom of the viewport so buyers always have a one-tap CTA
          without scrolling back up to the sidebar. Hidden from lg+
          where the sidebar is already sticky. Bottom-padding on the
          page shell prevents the bar from covering the last card. */}
      {!hasAgency && (
        <div
          data-testid="ad-sticky-contact-bar"
          className="fixed inset-x-0 bottom-0 z-40 flex gap-2 px-4 py-3 bg-white/95 backdrop-blur border-t border-subtle shadow-[0_-4px_16px_rgba(15,23,42,0.06)] lg:hidden"
        >
          <button
            data-testid="ad-sticky-show-phone"
            onClick={handleReveal}
            disabled={revealMut.isPending}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-bold text-white bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] disabled:opacity-70 transition-colors"
          >
            {revealMut.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Phone className="w-4 h-4" />
            )}
            {revealed?.phone ? revealed.phone : user ? "Show phone" : "Sign in to view contact details"}
          </button>
          {listing.allow_chat && (
            <button
              data-testid="ad-sticky-chat"
              onClick={() =>
                user
                  ? startChat.mutate()
                  : nav(`/login?next=${encodeURIComponent(window.location.pathname)}`)
              }
              disabled={startChat.isPending}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-bold text-[color:var(--brand-blue)] bg-[color:var(--brand-blue-wash)] border border-[color:var(--brand-blue)] transition-colors"
            >
              {startChat.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <MessageSquare className="w-4 h-4" />
              )}
              Chat
            </button>
          )}
        </div>
      )}

      {lightboxOpen && (
        <ImageLightbox
          images={images}
          index={activeImg}
          onIndexChange={setActiveImg}
          onClose={() => setLightboxOpen(false)}
        />
      )}
      {/* Iter-276 PART 1 — Messenger-style seller chat FAB. Self-hides
          on own ad, chat-off ads, and anon-gates via the login next-URL. */}
      <SellerChatFab listing={listing} />
    </div>
  );
}

function LoadingState() {
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-4">
          <div className="skeleton aspect-[16/10] rounded-3xl" />
          <div className="skeleton h-8 w-3/4" />
          <div className="skeleton h-4 w-1/2" />
          <div className="skeleton h-24 w-full" />
        </div>
        <div className="lg:col-span-4 space-y-4">
          <div className="skeleton h-64 w-full rounded-3xl" />
        </div>
      </div>
    </div>
  );
}

function NotFound({ status = 404 }) {
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-center">
      <PrerenderStatus code={status} />
      <h1 className="font-display text-3xl font-bold text-slate-900">Ad not found</h1>
      <p className="mt-2 text-slate-500">It may have expired or been removed.</p>
      <Link to="/" className="mt-6 inline-block rounded-full bg-[color:var(--brand-blue)] text-white px-5 py-2.5 font-semibold">
        Back to home
      </Link>
    </div>
  );
}
